import React, { useEffect, useRef, useState } from 'react';
import { resizeImage } from '../utils/resizeImage';
import './EntityIcon.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

// Emoji choices offered by the picker. Superset of the server's default palette
// so users can pick something different from the auto-assigned default.
const EMOJI_CHOICES = [
  '🚀',
  '🌟',
  '🎯',
  '💡',
  '🔥',
  '🌈',
  '🎨',
  '🧩',
  '📌',
  '🛠️',
  '🌱',
  '⚡',
  '🧭',
  '🪐',
  '🎵',
  '📦',
  '🔭',
  '🧠',
  '🏆',
  '🌊',
  '📊',
  '📝',
  '🗂️',
  '🔔',
  '🎲',
  '🧪',
  '🌍',
  '💬',
  '✅',
  '⭐',
];

/** True when an icon value is an uploaded image (URL) rather than an emoji. */
export function isImageIcon(value) {
  return typeof value === 'string' && (value.startsWith('/') || value.startsWith('http'));
}

/**
 * Renders an entity icon (emoji or uploaded image) inside a fixed-size box so
 * it never grows its container. Falls back to the first letter of `name` when
 * there is no icon value.
 */
export function EntityIcon({ icon, name, size = 22, className = '' }) {
  const style = { width: size, height: size };
  if (isImageIcon(icon)) {
    const src = icon.startsWith('http') ? icon : `${SERVER_URL}${icon}`;
    return (
      <span
        className={`entity-icon entity-icon-image ${className}`}
        style={style}
        aria-hidden="true"
      >
        <img src={src} alt="" />
      </span>
    );
  }
  const glyph =
    typeof icon === 'string' && icon.trim()
      ? icon
      : (name || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      className={`entity-icon ${className}`}
      style={{ ...style, fontSize: Math.round(size * 0.6) }}
      aria-hidden="true"
    >
      {glyph}
    </span>
  );
}

/** Emoji grid + image upload popover. Calls `onSelect` with the chosen value. */
function IconPicker({ onSelect }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const token = localStorage.getItem('symphonate-token');
      // Icons display tiny — shrink hard before upload to keep them well under
      // the 2MB cap and save storage.
      const resized = await resizeImage(file, { maxDimension: 512, quality: 0.85 });
      const formData = new FormData();
      formData.append('file', resized);
      const res = await fetch(`${SERVER_URL}/api/upload-icon`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Upload failed');
      }
      const data = await res.json();
      onSelect(data.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="icon-picker" role="dialog" aria-label="Choose an icon">
      <div className="icon-picker-grid">
        {EMOJI_CHOICES.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="icon-picker-emoji"
            onClick={() => onSelect(emoji)}
            title={`Use ${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </div>
      <div className="icon-picker-footer">
        <button
          type="button"
          className="icon-picker-upload"
          onClick={() => fileRef.current && fileRef.current.click()}
          disabled={uploading}
          title="Images up to 2MB; larger ones are automatically resized"
        >
          {uploading ? 'Uploading…' : 'Upload image'}
        </button>
        <span className="icon-picker-hint">PNG/JPG · max 2MB</span>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
      </div>
      {error && <div className="icon-picker-error">{error}</div>}
    </div>
  );
}

/**
 * The entity icon rendered as a button that opens the picker. When `canEdit` is
 * false it renders a plain, non-interactive icon. `onChange` receives the new
 * icon value (an emoji or an uploaded image URL).
 */
export function EditableIcon({ icon, name, size = 22, canEdit = false, onChange, title }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!canEdit) {
    return <EntityIcon icon={icon} name={name} size={size} />;
  }

  return (
    <span className="editable-icon" ref={ref}>
      <button
        type="button"
        className="editable-icon-btn"
        onClick={() => setOpen((v) => !v)}
        title={title || 'Change icon'}
        aria-label={title || 'Change icon'}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <EntityIcon icon={icon} name={name} size={size} />
      </button>
      {open && (
        <IconPicker
          onSelect={(value) => {
            if (onChange) onChange(value);
            setOpen(false);
          }}
        />
      )}
    </span>
  );
}
